import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { editingDataEmployeeService } from '../services/editingDataEmployee';
import { notifySuccess, notifyError } from '@config/tostifyConfig/tostifyConfig';

function parseBoundedDigits(raw, maxLen, maxValue) {
    const digits = String(raw).replace(/\D/g, '');
    if (digits === '') return 0;
    // Single-digit fields: value "0" + new key → "07"; keep last digit, not first
    const slice = maxLen === 1 ? digits.slice(-1) : digits.slice(0, maxLen);
    return Math.min(parseInt(slice, 10), maxValue);
}

export default function EmployerDataEditing({ person, onClose }) {
    const { t } = useTranslation();
    const [idn, setIdn] = useState('');
    const [nationality, setNationality] = useState('');
    const [fullName, setFullName] = useState('');
    const [years, setYears] = useState(0);
    const [months, setMonths] = useState(0);
    const [days, setDays] = useState(0);
    const [hours, setHours] = useState(0);
    const [saveLoading, setSaveLoading] = useState(false);
    const [initialRecordId, setInitialRecordId] = useState(null);

    const handleSave = async () => {
        const personId = person?.personId ?? person?.id;
        if (personId == null) {
            notifyError(t('common.errorMessage'));
            return;
        }
        setSaveLoading(true);
        try {
            if (initialRecordId) {
                await editingDataEmployeeService.updatePensionableServiceInitial(initialRecordId, {
                    years,
                    months,
                    days,
                    hours,
                });
            } else {
                await editingDataEmployeeService.savePensionableServiceInitial({
                    personId: Number(personId),
                    years,
                    months,
                    days,
                    hours,
                });
            }
            notifySuccess(t('common.updatedSuccessfully'));
        } catch (error) {
            const msg =
                error?.response?.data?.message ||
                (typeof error?.response?.data === 'string' ? error.response.data : null) ||
                t('common.errorMessage');
            notifyError(msg);
        } finally {
            setSaveLoading(false);
        }
    };

    useEffect(() => {
        const id = person?.personId ?? person?.id;
        if (!id) return;

        (async () => {
            const [initial, p] = await Promise.all([
                editingDataEmployeeService.getPensionableServiceInitial(id),
                editingDataEmployeeService.getPersonForPensionable(id),
            ]);

            setIdn(p?.idn ?? '');
            setNationality(p?.nationality ?? '');
            setFullName(p?.fullName ?? '');
            setInitialRecordId(initial?.id ?? null);
            setYears(Math.min(Math.max(initial?.years ?? 0, 0), 99));
            setMonths(Math.min(Math.max(initial?.months ?? 0, 0), 11));
            setDays(Math.min(Math.max(initial?.days ?? 0, 0), 30));
            setHours(Math.min(Math.max(initial?.hours ?? 0, 0), 7));
        })();
    }, [person?.personId, person?.id]);

    return (
        <div className="main-person-form-container">
            <h3 className="form-subtitle">
                {t('persons.dataEditing.title', {
                    name: [person.name, person.fathersName, person.surname]
                        .filter(Boolean)
                        .join(' '),
                })}
            </h3>
            <div className="flex-row position-center form-border-radius">
                {/* 1 */}
                <div className="flex-column gap-10">
                    <span>{t('persons.dataEditing.idn')}</span>
                    <span>{t('persons.dataEditing.citizenship')}</span>
                    <span>{t('persons.dataEditing.name')}</span>
                    <span>{t('persons.dataEditing.appointedWithSocialSecurityLengthOfService')}</span>
                </div>
                {/* 2 */}
                <div className="flex-column gap-10">
                    <div className="flex-row">
                        <input
                            type="text"
                            className="small-input width-20 input-disabled"
                            value={idn}
                            readOnly
                        />
                        <span className="margin-left-auto"> {t('persons.dataEditing.ServiceNumber')}: {person.empId}</span>
                    </div>
                    <input
                        type="text"
                        className="small-input width-30 input-disabled"
                        value={nationality}
                        readOnly
                    />
                    <input
                        type="text"
                        className="small-input width-50 input-disabled"
                        value={fullName}
                        readOnly
                    />
                    <div className="flex-row">
                        <span>{t('persons.dataEditing.years')}</span>
                        <input
                            type="text"
                            className="small-input width-10"
                            inputMode="numeric"
                            maxLength={2}
                            value={years}
                            onChange={(e) => setYears(parseBoundedDigits(e.target.value, 2, 99))}
                        />
                        <span>{t('persons.dataEditing.months')}</span>
                        <input
                            type="text"
                            className="small-input width-10"
                            inputMode="numeric"
                            maxLength={2}
                            value={months}
                            onChange={(e) => setMonths(parseBoundedDigits(e.target.value, 2, 11))}
                        />
                        <span>{t('persons.dataEditing.days')}</span>
                        <input
                            type="text"
                            className="small-input width-10"
                            inputMode="numeric"
                            maxLength={2}
                            value={days}
                            onChange={(e) => setDays(parseBoundedDigits(e.target.value, 2, 30))}
                        />
                        <span>{t('persons.dataEditing.hours')}</span>
                        <input
                            type="text"
                            className="small-input width-10"
                            inputMode="numeric"
                            value={hours}
                            onChange={(e) => setHours(parseBoundedDigits(e.target.value, 1, 7))}
                        />
                    </div>
                </div>
            </div>
            <div className="form-controls">
                <button
                    type="button"
                    className="form-btn btn-blue width-5"
                    disabled={saveLoading}
                    onClick={handleSave}
                >
                    {saveLoading ? t('common.saving') : t('common.save')}
                </button>
                <button className="form-btn btn-red width-5" onClick={onClose}>
                    {t('common.exit')}
                </button>
            </div>
        </div>
    );
}
